import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

@Component({
  selector: 'number-input',
  template: `
    <input
      [attr.type]="type"
      [attr.name]="name"
      [value]="value"
      (input)="onInputChange($event)"
      (focus)="focus.emit($event)"
      (blur)="onBlur()"
      (change)="change.emit($event)"
      (keydown)="onKeyDown($event)"
      [attr.min]="min"
      [attr.max]="max"
      [attr.step]="step"
      [attr.placeholder]="placeholder"
      [attr.disabled]="disabled ? true : null"
      [ngClass]="inputClass"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
  ],
})
export class NumberInputComponent implements ControlValueAccessor {
  @Input() name?: number
  @Input() min?: number
  @Input() max?: number
  @Input() step?: number
  @Input() placeholder?: string
  @Input() disabled = false
  @Input() type = 'text'
  @Input() inputClass: string | string[] | Set<string> = ''
  @Input() value = '' // Added for standalone usage with @Input

  // Two-way binding output for standalone usage
  @Output() valueChange = new EventEmitter<string>()

  @Output() focus = new EventEmitter<Event>()
  @Output() blur = new EventEmitter<Event>()
  @Output() change = new EventEmitter<Event>()

  private onChangeFn: (value: any) => void = () => {}
  private onTouched: () => void = () => {}

  // Updated regex: allows decimals and ensures only one decimal point
  private numberPattern = /^-?\d*\.?\d*$/

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {}

  onInputChange(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const newValue = inputElement.value

    // Validate if the input matches the number pattern
    if (this.numberPattern.test(newValue)) {
      this.value = newValue
      this.onChangeFn(newValue) // Notify Angular forms of the change
      this.valueChange.emit(newValue) // Notify parent component for standalone usage
    } else {
      inputElement.value = this.value // Reset to the previous valid value if the new value is invalid
    }
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
      'Home',
      'End',
      '-',
      '.',
    ]
    const isCtrlOrMeta = event.ctrlKey || event.metaKey
    if (
      !allowedKeys.includes(event.key) &&
      !(event.key >= '0' && event.key <= '9') &&
      !(isCtrlOrMeta && (event.key === 'v' || event.key === 'c' || event.key === 'a' || event.key === 'x'))
    ) {
      event.preventDefault()
    }
  }

  onBlur() {
    this.onTouched() // Mark as touched for Angular forms
    this.blur.emit() // Emit blur event for standalone usage
  }

  // ControlValueAccessor interface methods
  writeValue(value: any): void {
    this.value = value === undefined ? '' : value
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
}
